// Legacy identity coercions for version-skewed engines (read by the chat,
// contact, group and participant schemas). Its own module so those schemas
// can import each other without a cycle.

import { chatKindOf } from './chat-kind';

/**
 * MIGRATION COMPAT (2026-07-06) — normalize a legacy chat/contact identity
 * onto the canonical `chatId`. Gateway pods built before the #63/#64
 * `id`→`chatId` rename (image ≤ 20260223) still emit the identity as `id`
 * (a bare JID string or a `{ _serialized }` object) or `_serialized`, never
 * `chatId`. The backend boundary parse (engine-dispatch.ts) now REQUIRES
 * `chatId`, so a version-skewed gateway — pods routinely lag a backend deploy
 * — made GET /v1/chats · /v1/contacts · get-chat return 502
 * (`chats.0.chatId: Required`). Coerce the legacy shape here, at the single
 * producer schemas the boundary parses (BtSlimChat / BtSlimContact), so the
 * whole deployed fleet works. The engine SOURCE already emits `chatId`; once
 * every pod ships the post-rename image this coercion is a transparent no-op.
 * Kept deliberately narrow: it only fills `chatId` when absent and never
 * overwrites a present value, so it cannot mask a genuinely malformed row.
 */
function readLegacyId(o: Record<string, unknown>, snakeAlias?: string): string | undefined {
  // Pre-camelCase gateway images emit the identity in snake_case
  // (`chat_id` on participants, `group_id` on groups) — prefer that.
  if (snakeAlias && typeof o[snakeAlias] === 'string') return o[snakeAlias] as string;
  const id = o.id;
  return typeof id === 'string'
    ? id
    : id && typeof id === 'object' && typeof (id as { _serialized?: unknown })._serialized === 'string'
      ? (id as { _serialized: string })._serialized
      : typeof o._serialized === 'string'
        ? o._serialized
        : undefined;
}

/** Fill `chatId` from a legacy `id`/`_serialized`/`chat_id` when absent
 *  (chats, contacts, participants). No-op once the emitter uses `chatId`. */
export function coerceLegacyChatId(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const o = raw as Record<string, unknown>;
  if (o.chatId != null) return raw;
  const legacy = readLegacyId(o, 'chat_id');
  return legacy === undefined ? raw : { ...o, chatId: legacy };
}

/** Fill `groupId` from a legacy `id`/`_serialized`/`group_id` when absent
 *  (groups). Same version-skew shim as coerceLegacyChatId; no-op once the
 *  emitter uses `groupId`. */
export function coerceLegacyGroupId(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const o = raw as Record<string, unknown>;
  if (o.groupId != null) return raw;
  const legacy = readLegacyId(o, 'group_id');
  return legacy === undefined ? raw : { ...o, groupId: legacy };
}

/** Fill `chatType` from a legacy `isGroup`/`isNewsletter` pair — or, failing
 *  that, the JID suffix — when a version-skewed gateway (image predating the
 *  `chatType` emit) omits it. WhatsApp JIDs deterministically encode the kind:
 *  `@g.us` = group, `@newsletter` = newsletter, everything else (incl. `@c.us`
 *  and `@lid`) = contact. Same version-skew shim as coerceLegacyChatId — it
 *  kept the boundary parse from 502-ing on the `id`→`chatId` rename; this does
 *  the same for the isGroup/isNewsletter→chatType change, which otherwise made
 *  GET /v1/chats · /v1/contacts · /v1/search return 502 (`chats.N.chatType:
 *  Required`) for any gateway pod older than the chatType emit. No-op once the
 *  emitter sends `chatType`; never overwrites a present value. */
export function coerceLegacyChatType(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const o = raw as Record<string, unknown>;
  if (o.chatType != null) return raw;
  const kind = chatKindOf((typeof o.chatId === 'string' ? o.chatId : undefined) ?? readLegacyId(o, 'chat_id'));
  const isNewsletter = o.isNewsletter === true || kind === 'channel';
  const isGroup = o.isGroup === true || kind === 'group';
  const chatType = isNewsletter ? 'newsletter' : isGroup ? 'group' : 'contact';
  return { ...o, chatType };
}
